import type { CardPreviewDensity } from "@/components/shapes/card-previews";
import { GadgetCard } from "@/components/shapes/shared/card-visual-parts";
import { OPENED_PART } from "@/components/shapes/shared/object-frame";
import { DayIcon } from "./weather-day-icon";
import { inferConditionIcon } from "./weather-days";
import { weatherRange, type WeatherFields } from "./weather-fields";
import { useT } from "@/i18n";
import { weatherFrameMessages } from "@/i18n/messages/frames-weather";

/**
 * Weather (mockup `.wx`), the card and its opened window: the place, the
 * temperature, condition and range, and the next days when they fit. Opened,
 * each day also shows its sky and its low.
 */
export function WeatherFace({
  fields,
  density,
  paint,
  opened = false,
  testIdPrefix,
}: {
  fields: WeatherFields;
  density: CardPreviewDensity;
  /** The card's paint; an opened face wears its frame's. */
  paint?: string;
  opened?: boolean;
  /** The card's test id (`weather-card`), the prefix of its parts' ids. */
  testIdPrefix: string;
}) {
  const t = useT(weatherFrameMessages);
  const { place, temp, condition, days } = fields;
  const summary = [condition, weatherRange(fields, t)]
    .filter(Boolean)
    .join(" · ");
  return (
    <GadgetCard
      paint={paint}
      density={density}
      className={opened ? OPENED_PART : undefined}
      data-testid={opened ? `${testIdPrefix}-face` : testIdPrefix}
    >
      <p className="flex min-w-0 items-center gap-[0.45em] text-[0.9em] font-medium text-[color:var(--gadget-ink-soft)]">
        <DayIcon
          name={inferConditionIcon(condition ?? "")}
          className="h-[1.1em] w-[1.1em] shrink-0"
        />
        <span className="truncate" data-testid={`${testIdPrefix}-place`}>
          {place}
        </span>
      </p>
      <div className="min-w-0">
        {temp !== null && (
          <p
            className="font-display text-[3.6em] font-medium leading-none tracking-[-0.04em] tabular-nums"
            data-testid={`${testIdPrefix}-temp`}
          >
            {temp}°
          </p>
        )}
        {summary && (
          <p
            className="mt-[0.4em] truncate text-[0.9em] text-[color:var(--gadget-ink-soft)]"
            data-testid={`${testIdPrefix}-summary`}
          >
            {summary}
          </p>
        )}
      </div>
      {days.length > 0 && (
        <div
          className="flex justify-between text-[0.84em] text-[color:var(--gadget-ink-soft)]"
          data-testid={`${testIdPrefix}-days`}
        >
          {days.map((day, index) => (
            <span
              key={`${day.day}-${index}`}
              className="grid justify-items-center gap-[0.2em]"
              data-testid={`${testIdPrefix}-day-${index}`}
            >
              {day.day}
              {opened && day.icon && (
                <DayIcon name={day.icon} className="h-[1.1em] w-[1.1em]" />
              )}
              <b className="font-medium text-[color:var(--gadget-ink)]">
                {day.high}°
              </b>
              {opened && <span className="tabular-nums">{day.low}°</span>}
            </span>
          ))}
        </div>
      )}
    </GadgetCard>
  );
}
